import { Dispatch, ReactNode, SetStateAction } from 'react';
import { Divider, Stack } from '@common-components';
import { HeraldApplicationResponse } from 'clients/types';
import { RowsPerPage, useGenerateSuggestedProductsContent } from 'hooks';
import { messages } from 'i18n';
import { TeamInsuranceProduct } from 'types';
import NoItemsFound from 'broker/components/common/NoItemsFound';
import TablePagination from 'broker/components/common/TablePagination';
import { AIButtonWithPopup } from 'broker/pages/SubmissionWorkspacePage/components/AIGeneratedContent/AIButtonWithPopup';
import { SelectedTeamProductsMap } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/AddProducts/types';
import { useAIContext } from 'broker/pages/SubmissionWorkspacePage/contexts/ai/AIContextProvider';
import { useCreateAIThread } from 'broker/pages/SubmissionWorkspacePage/hooks';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import { ProductsTableView } from './ProductsTableView';
import RemarketingEligibility from './RemarketingEligibility';
import SubmissionRiskAssessment from './SubmissionRiskAssessment';

interface ProductsProps {
  items: TeamInsuranceProduct[];
  selectedTeamProducts: SelectedTeamProductsMap;
  onTeamProductSelected: (product: TeamInsuranceProduct) => void;
  onTeamProductRemoved: (productId: string) => void;
  isLoadingTeamProducts: boolean;
  sortedAndFilteredItems: TeamInsuranceProduct[];
  search: string;
  setPage: Dispatch<SetStateAction<number>>;
  page: number;
  rowsPerPage: number;
  setRowsPerPage: Dispatch<SetStateAction<RowsPerPage>>;
  heraldApplicationResponse?: HeraldApplicationResponse;
  header?: ReactNode;
}

export function Products({
  items,
  selectedTeamProducts,
  onTeamProductSelected,
  onTeamProductRemoved,
  sortedAndFilteredItems,
  isLoadingTeamProducts,
  search,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  header,
  heraldApplicationResponse,
}: ProductsProps) {
  const showItems = isLoadingTeamProducts || items.length > 0;

  const handleChange = (value: number) => {
    setPage(value);
  };

  const { submission } = useSubmissionsWorkspace();
  const { generateSuggestedProductsContent } = useGenerateSuggestedProductsContent();

  const { isLoading: isAIContentLoading, isContentStreaming } = useAIContext();

  useCreateAIThread(() => generateSuggestedProductsContent({ submissionId: submission!.id }), false);

  const productsSlice = sortedAndFilteredItems.slice(page * rowsPerPage - rowsPerPage, page * rowsPerPage);

  return (
    <Stack height={1}>
      <AIButtonWithPopup
        popupTitle={messages.submissionWorkspace.recommendedProducts}
        tooltipText={
          isAIContentLoading && !isContentStreaming
            ? messages.submissionWorkspace.generatingProducts
            : messages.submissionWorkspace.recommendationsReady
        }
      />
      <Stack flex={1}>
        {header}
        <RemarketingEligibility />
        <SubmissionRiskAssessment submission={submission} />
        {showItems && (
          <ProductsTableView
            quoteExits={heraldApplicationResponse?.quote_exits}
            teamProducts={productsSlice}
            selectedProducts={selectedTeamProducts}
            onProductSelected={onTeamProductSelected}
            onProductRemoved={onTeamProductRemoved}
            isLoading={isLoadingTeamProducts}
            searchInput={search}
            bottomTableContent={
              <Stack>
                <Divider />
                <TablePagination
                  from={page * rowsPerPage - rowsPerPage + 1}
                  to={page * rowsPerPage}
                  outOf={sortedAndFilteredItems.length}
                  handleChange={handleChange}
                  count={Math.ceil(sortedAndFilteredItems.length / rowsPerPage)}
                  page={page}
                  setPage={setPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  overrideContainerStyle={{ borderRadius: 0, bgcolor: 'common.white' }}
                />
              </Stack>
            }
          />
        )}
        {!showItems && <NoItemsFound title={messages.marketsPage.emptyState} />}
      </Stack>
    </Stack>
  );
}
